/**
 * Parallax leve baseado em scroll. Sem libs.
 * - <Parallax speed={0.3}>...</Parallax>
 *   speed positivo: elemento sobe mais devagar que o scroll (sensação de profundidade)
 *   speed negativo: elemento desce conforme rola (mais perto da câmera)
 *
 * Usa rAF + IntersectionObserver pra evitar update fora da viewport.
 */
import { useEffect, useRef, type ReactNode, type CSSProperties } from "react";

type Props = {
  children: ReactNode;
  speed?: number; // -1..1 típico
  className?: string;
  style?: CSSProperties;
  as?: keyof JSX.IntrinsicElements;
};

export function Parallax({ children, speed = 0.2, className, style, as = "div" }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const visibleRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      rafRef.current = null;
      if (!visibleRef.current || !el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // centro do elemento relativo ao centro da viewport (-1 acima, +1 abaixo)
      const center = (rect.top + rect.height / 2 - vh / 2) / vh;
      const offset = center * speed * 100; // px
      el.style.transform = `translate3d(0, ${offset}px, 0)`;
    };

    const onScroll = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(update);
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          visibleRef.current = e.isIntersecting;
          if (e.isIntersecting) onScroll();
        }
      },
      { rootMargin: "20% 0px 20% 0px" }
    );
    io.observe(el);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [speed]);

  const Tag = as as any;
  return (
    <Tag ref={ref} className={className} style={{ willChange: "transform", ...style }}>
      {children}
    </Tag>
  );
}
